<script lang="ts">
  import Icon from '../Icon/Icon.svelte';
  import SelectableList from './SelectableList.svelte';
  import DateRange from './DateRange.svelte';
  import { DropdownMenuType } from '$shared/constants/DropdownMenuType';

  const DefaultLabel = 'Select';

  interface Props {
    data?: any;
    disabled?: boolean;
    label?: string;
    type?: DropdownMenuType;
    name?: string;
    onchange?: (detail: any) => void;
    onclear?: () => void;
  }

  let {
    data = null,
    disabled = false,
    label = '',
    type = null,
    name = '',
    onchange,
    onclear
  }: Props = $props();

  let parent: HTMLDivElement;
  let isActive = $state(false);
  let showList = $state(false);
  let selectableListElement: SelectableList | undefined = $state();

  const close = (): void => {
    showList = false;
    selectableListElement?.reset();
  };

  /**
   * Scoped to the wrapper so the trigger button and the list body share one
   * listener. Enter/Space are left to the trigger's native click when closed.
   */
  const onKeyDown = (e: KeyboardEvent): void => {
    if (disabled) return;

    if (!showList) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        showList = true;
      }
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close();
      return;
    }

    if (e.key === 'Tab') {
      // Focus is leaving the list (e.g. to the Apply button); drop the
      // highlight so Enter there applies instead of toggling a row.
      selectableListElement?.reset();
      return;
    }

    if (selectableListElement?.handleKeyDown(e)) {
      e.preventDefault();
    }
  };

  const onLabelMouseEnter = (): void => {
    isActive = true;
  };

  const onLabelMouseLeave = (): void => {
    isActive = false;
  };

  const onClickLabel = (): void => {
    showList = !showList;
  };

  const onDateRangeChange = (detail: any): void => {
    close();
    onchange?.(detail);
  };

  const onSelectableListChange = (detail: CommonList[]): void => {
    close();
    onchange?.(detail);
  };

  const onClickWindow = (e: any): void => {
    if (parent && !parent.contains(e.target)) {
      close();
    }
  };
</script>

<svelte:window onmouseup={onClickWindow} />

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div data-cy="dropdown-menu" bind:this={parent} class="dropdown-menu" onkeydown={onKeyDown}>
  <button
    class="label"
    data-cy={name}
    {disabled}
    aria-haspopup="true"
    aria-expanded={showList}
    onmouseenter={onLabelMouseEnter}
    onmouseleave={onLabelMouseLeave}
    onclick={onClickLabel}
  >
    <div class="text">{label || DefaultLabel}</div>
    <div class="icon"><Icon value="icon-chevron-down {(isActive && 'active') || ''}" /></div>
  </button>
  <div data-cy="menu-list-container" class="container" class:show={showList}>
    {#if type === DropdownMenuType.SelectableList}
      <SelectableList
        {disabled}
        {data}
        bind:this={selectableListElement}
        onchange={onSelectableListChange}
      />
    {:else if type === DropdownMenuType.DateRange}
      <DateRange {disabled} onchange={onDateRangeChange} {onclear} />
    {/if}
  </div>
</div>

<style>
  .dropdown-menu {
    position: relative;
    z-index: 99;
  }
  .container {
    display: none;
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 1;
    background-color: #fff;
  }
  .container.show {
    display: block;
  }
  .label {
    background-color: #fff;
    border: 1px solid #e8eaef;
    box-shadow: 1px 2px 3px rgba(0, 0, 0, 0.05);
    border-radius: 4px;
    height: 30px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 12px;
    font-weight: normal;
    font-size: 14px;
    line-height: 22px;
    color: var(--grey-dark);
  }
  .label:disabled {
    background-color: var(--grey-light);
    color: #fff;
    cursor: not-allowed;
  }
  .icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: 8px;
    margin-bottom: -2px;
  }
  .label:not(:disabled):hover {
    background-color: var(--light-blue);
    color: var(--blue);
  }
</style>
